import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import * as React from 'react'
import { sessionQuery } from '../../lib/api/queries'
import { type BrandScopedMe, type BrandSummary, validBrandScope } from './api'

export interface BrandScopeState {
  /** The brand the queue shows: a slug from `brands`, or `all`. */
  selected: string
  selectedBrand: BrandSummary | null
  /** Every brand this person can see (`MeOut.brands`), paused ones included. */
  brands: BrandSummary[]
  isAll: boolean
  /**
   * The `?brand=` to send to the queue API. Undefined when nothing is selected (no URL brand, saved scope `all`):
   * the request then carries no brand and is exactly today's.
   */
  param: string | undefined
  /**
   * Whether brand labels (row chips, lane breakdowns, the case header chip and the composer sender line) are worth
   * showing: more than one active brand, or a brand is selected. With only Ewee live and nothing selected, the shell is
   * exactly the single-brand UI it was before brands.
   */
  showBrands: boolean
}

export function resolveBrandScope(me: BrandScopedMe | null | undefined, requested?: unknown): BrandScopeState {
  const brands = me?.brands ?? []
  const available = new Set(brands.map((brand) => brand.slug))
  const fromUrl = validBrandScope(requested)
  const preferred = validBrandScope(me?.brand_scope)
  const urlChoice = fromUrl && (fromUrl === 'all' || available.has(fromUrl)) ? fromUrl : undefined
  const selected = urlChoice ?? (preferred && available.has(preferred) ? preferred : 'all')
  const isAll = selected === 'all'
  const active = brands.filter((brand) => brand.status === 'active').length
  return {
    selected,
    selectedBrand: brands.find((brand) => brand.slug === selected) ?? null,
    brands,
    isAll,
    param: urlChoice ?? (isAll ? undefined : selected),
    showBrands: active > 1 || !isAll,
  }
}

const noop = () => {}

/** `?brand=` from the router's current location. Outside a router (isolated component tests) there is none. */
function useUrlBrand(): unknown {
  const router = useRouter({ warn: false }) as ReturnType<typeof useRouter> | null
  const subscribe = React.useCallback((onChange: () => void) => (router ? router.subscribe('onResolved', onChange) : noop), [router])
  return React.useSyncExternalStore(subscribe, () => (router ? (router.state.location.search as Record<string, unknown>).brand : undefined))
}

/** Never fetched from: it only stands in when a component renders without a QueryClientProvider. */
const NO_CLIENT = new QueryClient()

/**
 * The signed-in `MeOut` (the same session query `useSession` reads). Brand labels sit inside widely reused pieces such
 * as the case header and queue row, which are also rendered alone in tests; there, no provider means no brands.
 */
function useSessionMe(): BrandScopedMe | null {
  const client = React.useContext(QueryClientContext)
  const session = useQuery({ ...sessionQuery(), enabled: client !== undefined }, client ?? NO_CLIENT)
  // A failed session read is the shell's to show (useSession's unknown state); brand labels stay off until it answers.
  if (session.isError && !session.data) return null
  return session.data?.signed_in && session.data.me ? session.data.me : null
}

export function useBrandScope(): BrandScopeState {
  const me = useSessionMe()
  const brand = useUrlBrand()
  return resolveBrandScope(me, brand)
}
